// Updated use-architecture.tsx with conditional loading support
import { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArchitectureComponent, EditItem } from "@/types/unified-model";
import { getDocumentsSignedUrlById } from "@/service-hooks/use-get-document-service";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { toast } from "sonner";
import {
  useNeo4jGraphService,
  categoryToLabel,
  // NOTE: useGetGraph is now called from the parent component
} from "@/service-hooks/use-neo4j-graph-service";
import { config } from "@/config";
import { apiFetch } from "@/lib/api-client";

export function useArchitecture(
  graphData?: any, // Accept graph data as prop from parent
  isLoadingGraph?: boolean // Accept loading state from parent
) {
  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthHeaders();
  const { id = "" } = useParams();
  
  // Neo4j service hooks
  const {
    useCreateNode,
    useUpdateNode,
    useDeleteNode,
    useUpsertGraph,
  } = useNeo4jGraphService(id);


  const [edit, setEdit] = useState<EditItem | null>(null);
  const [editMode, setEditMode] = useState<'add' | 'modify' | null>(null);
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Mutations
  const createNodeMutation = useCreateNode();
  const updateNodeMutation = useUpdateNode();
  const deleteNodeMutation = useDeleteNode();
  const upsertGraphMutation = useUpsertGraph();

  // Load category-specific data using the label-specific API
  const loadCategoryData = useCallback(
    async (category: string, page: number = 1, limit: number = 10) => {
      try {
        const headers = getAuthHeaders();
        const label = categoryToLabel(category);
                
        const response = await apiFetch(
          `${config.isometricApiUrl}/architectural-graph/${id}/${label}?page=${page}&limit=${limit}`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...headers,
            }
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`API Error for ${category}:`, errorText);
          const error: any = new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
          error.status = response.status;
          throw error;
        }

        const json = await response.json();
        
        return {
          data: json.data || [],
          hasMore: (json.data?.length || 0) === limit,
          total: json.total || 0,
        };
      } catch (error) {
        console.error(`Error loading ${category} data:`, error);
        toast.error(`Failed to load ${category} data: ${error instanceof Error ? error.message : error}`);
        return { data: [], hasMore: false, total: 0 };
      }
    },
    [id, getAuthHeaders]
  );

  // Delete a specific component
  const deleteCategoryItem = useCallback(
    async (category: string, componentId: string) => {
      try {
        const headers = getAuthHeaders();
        const label = categoryToLabel(category);
                
        const response = await apiFetch(
          `${config.isometricApiUrl}/architectural-graph/${id}/${label}/delete-node/${componentId}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              ...headers,
            },
          }
        );

        if (!response.ok) {
          const error: any = new Error(`HTTP ${response.status}: ${response.statusText}`);
          error.status = response.status;
          throw error;
        }

        const json = await response.json();
        toast.success("Component deleted successfully");
        
        // Invalidate graph cache to refresh counts
        queryClient.invalidateQueries({ queryKey: ["neo4j-graph", id] });
        
        return json.data?.deletedCount || 0;
      } catch (error) {
        console.error(`Error deleting component ${componentId}:`, error);
        toast.error("Failed to delete component");
        throw error;
      }
    },
    [id, getAuthHeaders, queryClient]
  );

  const handleStartEdit = (category: string, value: unknown[], mode: 'add' | 'modify' = 'modify') => {
    setEdit({
      key: "Architecture",
      value: [...value],
    });
    setSelectedCategory(category);
    setEditMode(mode);
  };

  const handleStartAdd = (category: string) => {
    handleStartEdit(category, [], 'add');
  };

  const handleStartModify = (category: string, value: unknown[]) => {
    handleStartEdit(category, value, 'modify');
  };

  const handleEntityClick = (entityName: string): void => {
    setSelectedEntity(entityName === selectedEntity ? null : entityName);
  };

  const handleCategoryClick = (category: string): void => {
    setSelectedCategory(category);
  };

  const handleDocumentDownload = async ({ documentId }: { documentId: string; documentName: string }) => {
    const headers = getAuthHeaders();
    const signedUrl = await queryClient.fetchQuery({
      queryKey: ["getSignedDocUrl", documentId],
      queryFn: () => getDocumentsSignedUrlById(documentId, headers),
      staleTime: 300000,
    });
    window.open(signedUrl, "_blank", "noopener,noreferrer");
  };

  // Updated save function to work with the upsert API
  const handleSaveChanges = async () => {
    if (!edit || !selectedCategory) return;

    const filteredValue = edit.value.filter((val) => {
      return val && typeof val === "object" && "name" in val && val.name;
    });

    try {
      if (editMode === 'add') {
        
        // Format data according to your API structure
        const upsertData = {
          project: { uuid: id },
          [selectedCategory]: filteredValue.map(item => ({
            name: item.name,
            description: item.description || "",
            scenarios: item.scenarios || [],
            ...Object.fromEntries(
              Object.entries(item).filter(([key]) => 
                !['id', 'group', 'metadata'].includes(key)
              )
            )
          }))
        };

        const headers = getAuthHeaders();
        const response = await apiFetch(
          `${config.isometricApiUrl}/architectural-graph/upsert`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...headers,
            },
            body: JSON.stringify(upsertData),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          const error: any = new Error(`Upsert failed: ${response.statusText} - ${errorText}`);
          error.status = response.status;
          throw error;
        }

        await response.json();
        
        toast.success("Components added successfully");
        
        // Force reload of the category data to show new components
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ["neo4j-graph", id] });
          // Trigger a custom event to refresh category data
          window.dispatchEvent(new CustomEvent('architecture-data-updated', { 
            detail: { category: selectedCategory } 
          }));
        }, 1000); // Increased delay to ensure API has processed
        
      } else if (editMode === 'modify') {
        const label = categoryToLabel(selectedCategory);
        
        // Handle updates and creates
        const updatePromises = filteredValue.map(async (item: any) => {
          
          const nodeData = { 
            name: item.name,
            description: item.description || "",
            scenarios: item.scenarios || [],
            group: selectedCategory,
            ...Object.fromEntries(
              Object.entries(item).filter(([key]) => 
                !['id', 'group', 'metadata'].includes(key)
              )
            )
          };

          if (item.id) {
            
            const headers = getAuthHeaders();
            const response = await apiFetch(
              `${config.isometricApiUrl}/architectural-graph/${id}/update-node/${item.id}`,
              {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  ...headers,
                },
                body: JSON.stringify({
                  label,
                  data: nodeData
                }),
              }
            );

            if (!response.ok) {
              const errorText = await response.text();
              const error: any = new Error(`Update failed: ${response.statusText} - ${errorText}`);
              error.status = response.status;
              throw error;
            }

            return response.json();
          } else {
            
            const headers = getAuthHeaders();
            const response = await apiFetch(
              `${config.isometricApiUrl}/architectural-graph/${id}/create-node`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...headers,
                },
                body: JSON.stringify({
                  label,
                  data: nodeData
                }),
              }
            );

            if (!response.ok) {
              const errorText = await response.text();
              const error: any = new Error(`Create failed: ${response.statusText} - ${errorText}`);
              error.status = response.status;
              throw error;
            }

            return response.json();
          }
        });

        await Promise.all(updatePromises);
        toast.success("Components updated successfully");
        
        // Force reload of the category data
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ["neo4j-graph", id] });
          window.dispatchEvent(new CustomEvent('architecture-data-updated', { 
            detail: { category: selectedCategory } 
          }));
        }, 500);
      }

    } catch (error) {
      console.error('Error saving architecture changes:', error);
      toast.error('Failed to save changes');
    } finally {
      setEdit(null);
      setEditMode(null);
      setSelectedEntity(null);
    }
  };

  // Update single component via popup editor
  const handleUpdateSingleComponent = async (updatedComponent: ArchitectureComponent) => {
    if (!updatedComponent.id) return;

    try {
      const label = categoryToLabel(updatedComponent.group || 'services');
      const nodeData = {
        name: updatedComponent.metadata?.blueprint?.name || updatedComponent.name,
        description: updatedComponent.metadata?.blueprint?.description || updatedComponent.description,
        scenarios: updatedComponent.metadata?.blueprint?.scenarios || updatedComponent.scenarios || [],
        group: updatedComponent.group,
        ...Object.fromEntries(
          Object.entries(updatedComponent.metadata?.blueprint || updatedComponent)
            .filter(([key]) => !['id', 'group', 'metadata'].includes(key))
        )
      };


      const headers = getAuthHeaders();
      const response = await apiFetch(
        `${config.isometricApiUrl}/architectural-graph/${id}/update-node/${updatedComponent.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...headers,
          },
          body: JSON.stringify({
            label,
            data: nodeData
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        const error: any = new Error(`Update failed: ${response.statusText} - ${errorText}`);
        error.status = response.status;
        throw error;
      }

      // Refresh graph data - the parent component will handle state updates via query invalidation
      queryClient.invalidateQueries({ queryKey: ["neo4j-graph", id] });

      toast.success("Component updated successfully");
    } catch (error) {
      console.error('Error updating component:', error);
      // toast.error('Failed to update component');
    }
  };

  return {
    selectedEntity,
    selectedCategory,
    edit,
    editMode,
    setEdit,
    setSelectedEntity,
    setSelectedCategory,
    handleEntityClick,
    handleCategoryClick,
    handleStartAdd,
    handleStartModify,
    handleSaveChanges,
    handleUpdateSingleComponent,
    handleDocumentDownload,
    loadCategoryData,
    deleteCategoryItem,
    isLoadingGraph, // Pass through from parent
    graphData, // Pass through from parent
    createNodeMutation,
    updateNodeMutation,
    deleteNodeMutation,
    upsertGraphMutation,
  };
}